/**
 * Console-only helpers for displaying uploaded JSON file contents
 * (Binary body + Form Data file fields). Does not affect request execution.
 */

/** Safety guard: do not silently truncate; warn clearly above this size. */
export const CONSOLE_JSON_FILE_MAX_BYTES = 5 * 1024 * 1024; // 5 MB

export function isJsonFile(file: { name?: string; type?: string }): boolean {
  const type = String(file.type || '').toLowerCase().trim();
  if (type === 'application/json' || type.includes('+json')) {
    return true;
  }
  const name = String(file.name || '').toLowerCase();
  return (
    name.endsWith('.json') ||
    name.endsWith('.geojson') ||
    name.endsWith('.jsonld')
  );
}

export type JsonFileConsoleReadResult = {
  /** Pretty-printed JSON, or original text when parse fails. Empty when too large. */
  content: string;
  parseError?: string;
  tooLarge?: boolean;
  sizeBytes: number;
  maxBytes?: number;
};

/**
 * Read a JSON file as text for Console display (not Base64).
 * Pretty-prints when valid JSON; otherwise returns original text + parse note.
 */
export async function readJsonFileForConsole(file: File): Promise<JsonFileConsoleReadResult> {
  const sizeBytes = file.size;
  if (sizeBytes > CONSOLE_JSON_FILE_MAX_BYTES) {
    return {
      content: '',
      tooLarge: true,
      sizeBytes,
      maxBytes: CONSOLE_JSON_FILE_MAX_BYTES,
    };
  }

  let text: string;
  if (typeof file.text === 'function') {
    text = await file.text();
  } else {
    text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () =>
        reject(reader.error instanceof Error ? reader.error : new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  try {
    const parsed = JSON.parse(text);
    return {
      content: JSON.stringify(parsed, null, 2),
      sizeBytes,
    };
  } catch {
    return {
      content: text,
      parseError:
        'File has a JSON extension/type, but the content is not valid JSON.',
      sizeBytes,
    };
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Human-readable Console block for an uploaded file (Binary or Form Data field).
 * Non-JSON files: metadata only. JSON files: includes FILE CONTENT when readable.
 */
export async function formatUploadedFileForConsole(
  file: File,
  fieldName?: string
): Promise<string> {
  const lines: string[] = [];
  if (fieldName != null && fieldName !== '') {
    lines.push(`Field: ${fieldName}`);
  }
  lines.push(`File name: ${file.name || '(unnamed file)'}`);
  lines.push(`Content-Type: ${file.type || 'application/octet-stream'}`);
  lines.push(`Size: ${formatBytes(file.size)}`);
  if (file.lastModified) {
    lines.push(`Last modified: ${new Date(file.lastModified).toISOString()}`);
  }

  if (!isJsonFile(file)) {
    return lines.join('\n');
  }

  const read = await readJsonFileForConsole(file);
  lines.push('');
  if (read.tooLarge) {
    lines.push(
      `JSON file content not shown: file size (${formatBytes(read.sizeBytes)}) exceeds the Console display limit of ${formatBytes(read.maxBytes ?? CONSOLE_JSON_FILE_MAX_BYTES)}. Content was not truncated.`
    );
    return lines.join('\n');
  }
  if (read.parseError) {
    lines.push(read.parseError);
    lines.push('');
  }
  lines.push('FILE CONTENT');
  lines.push(read.content);
  return lines.join('\n');
}

/** Structured metadata + optional JSON preview for proxy Console log objects. */
export async function buildJsonFileLogFields(
  file: File
): Promise<Record<string, unknown> | null> {
  if (!isJsonFile(file)) {
    return null;
  }
  const read = await readJsonFileForConsole(file);
  if (read.tooLarge) {
    return {
      contentTooLarge: true,
      contentLimitBytes: read.maxBytes ?? CONSOLE_JSON_FILE_MAX_BYTES,
      contentLimitNote: `JSON file content not shown: file size (${formatBytes(read.sizeBytes)}) exceeds the Console display limit of ${formatBytes(read.maxBytes ?? CONSOLE_JSON_FILE_MAX_BYTES)}. Content was not truncated.`,
    };
  }
  const fields: Record<string, unknown> = {
    fileContent: read.content,
  };
  if (read.parseError) {
    fields.parseError = read.parseError;
  }
  return fields;
}
